import React, { useState } from "react";
import { gql, useMutation } from "@apollo/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, LifeBuoy, Loader, Mail } from "lucide-react";

const REQUEST_PASSWORD_SUPPORT = gql`
  mutation RequestPasswordSupport($email: String!) {
    requestPasswordSupport(email: $email) {
      success
      message
    }
  }
`;

export const RequestPasswordReset = () => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [requestPasswordSupport, { loading }] = useMutation(REQUEST_PASSWORD_SUPPORT);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    try {
      await requestPasswordSupport({ variables: { email: email.trim() } });
      setSubmitted(true);
    } catch (_requestError) {
      setError("Unable to submit your request. Please try again.");
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center overflow-y-auto bg-[var(--surface-muted)] px-4 py-8">
      <div className="w-full max-w-[400px] overflow-hidden rounded-[var(--border-radius-large)] border border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)] shadow-[var(--shadow-light)]">
        <div className="flex items-center justify-between border-b border-[var(--neutral-gray-30)] bg-[var(--surface-muted)] px-6 py-3">
          <a id="password-support-dhs-link" href="https://dhs.lacounty.gov/" target="_blank" rel="noreferrer">
            <img
              src={`${process.env.PUBLIC_URL}/dhs-logo.svg`}
              alt="Los Angeles County Department of Health Services"
              className="h-7 w-auto"
            />
          </a>
          <a
            id="password-support-county-link"
            href="https://lacounty.gov/"
            target="_blank"
            rel="noreferrer"
            className="opacity-70 transition hover:opacity-100"
          >
            <img
              src={`${process.env.PUBLIC_URL}/lacountyseal.png`}
              alt="County of Los Angeles seal"
              className="h-7 w-7 object-contain"
            />
          </a>
        </div>

        <div className="space-y-6 px-6 py-6">
          <div className="space-y-3 text-center">
            <img
              src={`${process.env.PUBLIC_URL}/logoODR.png`}
              alt="Office of Diversion and Reentry"
              className="mx-auto h-16 w-auto"
            />
            <div className="text-2xl font-bold tracking-tight text-[var(--neutral-gray-120)]">
              Diversion Database
            </div>
          </div>

          <div className="space-y-5">
            <div className="text-center">
              <h1 className="text-xl font-semibold text-[var(--neutral-gray-120)]">Forgot your password?</h1>
              <p className="mt-2 text-sm text-[var(--neutral-gray-90)]">
                Enter the email address for your account.
              </p>
            </div>

            {!submitted && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="password-support-email" className="block text-xs font-medium text-[var(--neutral-gray-100)]">
                    Email address
                  </label>
                  <div className="flex h-10 items-center rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-50)] bg-[var(--surface-panel-solid)] transition focus-within:border-[var(--azure-primary)] focus-within:ring-2 focus-within:ring-[var(--azure-focus-ring)]">
                    <Mail className="mx-3 h-4 w-4 text-[var(--neutral-gray-80)]" />
                    <Input
                      id="password-support-email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      maxLength={254}
                      required
                      className="h-10 min-w-0 flex-1 border-0 bg-transparent px-0 pr-3 text-sm text-[var(--neutral-gray-120)] shadow-none focus-visible:border-transparent focus-visible:ring-0"
                    />
                  </div>
                </div>
                {error && <div className="text-center text-sm text-[var(--semantic-error)]">{error}</div>}
                <Button
                  id="password-support-submit"
                  type="submit"
                  disabled={loading || !email.trim()}
                  className="h-11 w-full text-base"
                >
                  {loading ? <Loader className="h-4 w-4 animate-spin" /> : <LifeBuoy className="h-4 w-4" />}
                  {loading ? "Submitting..." : "Contact Support"}
                </Button>
              </form>
            )}

            {submitted && (
              <div role="status" className="rounded-[var(--border-radius-medium)] border border-[var(--semantic-success)] bg-[var(--surface-muted)] p-4 text-center text-sm text-[var(--neutral-gray-110)]">
                If the account is active, Support will contact you.
              </div>
            )}
          </div>

          <div className="text-center">
            <a
              id="password-support-back-to-login"
              href="/login"
              className="inline-flex items-center gap-2 text-sm text-[var(--azure-primary)] hover:text-[var(--azure-primary-hover)]"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Login
            </a>
          </div>

          <div className="border-t border-[var(--neutral-gray-30)] pt-4 text-center text-xs leading-4 text-[var(--neutral-gray-90)]">
            <div>Los Angeles County Department of Health Services</div>
            <div>Office of Diversion and Reentry</div>
          </div>
        </div>
      </div>
    </div>
  );
};
